import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import { parseEntityRef } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.875rem',
    wordBreak: 'break-all',
  },
  chip: {
    height: 22,
    fontSize: '0.75rem',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
}));

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value} component="div">{children}</Typography>
    </div>
  );
}

const SUBTYPE_OVERRIDES: Record<string, string> = {
  'azure-key-vault': 'Azure Key Vault',
  'azure-storage-account': 'Azure Storage Account',
  'azure-data-factory': 'Azure Data Factory',
};

function formatSubType(subType: string): string {
  return SUBTYPE_OVERRIDES[subType] ?? subType
    .replace(/^azure-/, '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export const AzureResourceInfoCard = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const catalogEntityRoute = useRouteRef(entityRouteRef);

  const meta = entity.metadata as Record<string, any>;
  const spec = entity.spec as Record<string, any>;

  const subType: string = spec?.subType ?? '';
  const resourceType: string = meta.resourceType ?? subType;
  const resourceLabel = formatSubType(resourceType);

  const specDependsOn: string[] = (spec?.dependsOn ?? []) as string[];
  const environmentRef = specDependsOn.find(r => r.toLowerCase().startsWith('azureenvironment:'));

  // Support new boundComponents (comma-separated) and legacy boundComponent
  const rawBound: string = (meta.boundComponents as string) || (meta.boundComponent as string) || '';
  const boundRefs = rawBound ? rawBound.split(',').map((s: string) => s.trim()).filter(Boolean) : [];

  const boundLinks: React.ReactNode = boundRefs.length === 0
    ? <Chip label="Unbound" size="small" className={classes.chip} />
    : (
      <>
        {boundRefs.map(ref => {
          try {
            const parsed = parseEntityRef(ref, { defaultKind: 'component', defaultNamespace: 'default' });
            const href = catalogEntityRoute({ kind: parsed.kind, namespace: parsed.namespace ?? 'default', name: parsed.name });
            return <div key={ref}><Link to={href}>{parsed.name}</Link></div>;
          } catch {
            return <div key={ref}>{ref}</div>;
          }
        })}
      </>
    );

  return (
    <InfoCard title={resourceLabel} variant="gridItem">
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Field label="Resource Type">{resourceLabel}</Field>
        </Grid>
        {environmentRef && (
          <Grid item xs={12} sm={6}>
            <Field label="Environment">
              {(() => {
                try {
                  const parsed = parseEntityRef(environmentRef, { defaultKind: 'AzureEnvironment', defaultNamespace: 'default' });
                  const href = catalogEntityRoute({ kind: parsed.kind, namespace: parsed.namespace ?? 'default', name: parsed.name });
                  return <Link to={href}>{parsed.name}</Link>;
                } catch {
                  return <span>{environmentRef}</span>;
                }
              })()}
            </Field>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Field label={boundRefs.length > 1 ? 'Bound Applications' : 'Bound Application'}>{boundLinks}</Field>
        </Grid>

        {meta.azureRegion && (
          <Grid item xs={12} sm={6}>
            <Field label="Azure Region">{meta.azureRegion}</Field>
          </Grid>
        )}
        {meta.resourceGroupName && (
          <Grid item xs={12} sm={6}>
            <Field label="Resource Group">{meta.resourceGroupName}</Field>
          </Grid>
        )}

        {/* Key Vault specific */}
        {meta.vaultUri && (
          <Grid item xs={12}>
            <Field label="Vault URI">
              <Link to={`${meta.vaultUri}`} target="_blank" rel="noopener noreferrer">
                {meta.vaultUri}
              </Link>
            </Field>
          </Grid>
        )}
        {meta.vaultId && (
          <Grid item xs={12}>
            <Field label="Vault Resource ID">{meta.vaultId}</Field>
          </Grid>
        )}
        {meta.skuName && (
          <Grid item xs={12} sm={6}>
            <Field label="SKU">{String(meta.skuName)}</Field>
          </Grid>
        )}
        {typeof meta.purgeProtection !== 'undefined' && (
          <Grid item xs={12} sm={6}>
            <Field label="Purge Protection">
              <Chip
                label={meta.purgeProtection ? 'Enabled' : 'Disabled'}
                size="small"
                className={classes.chip}
                color={meta.purgeProtection ? 'primary' : 'default'}
              />
            </Field>
          </Grid>
        )}

        {/* Storage Account specific */}
        {meta.primaryBlobEndpoint && (
          <Grid item xs={12}>
            <Field label="Blob Endpoint">{meta.primaryBlobEndpoint}</Field>
          </Grid>
        )}
        {meta.primaryDfsEndpoint && (
          <Grid item xs={12}>
            <Field label="DFS (ADLS Gen2) Endpoint">{meta.primaryDfsEndpoint}</Field>
          </Grid>
        )}
        {meta.accountTier && (
          <Grid item xs={12} sm={6}>
            <Field label="Account Tier">{meta.accountTier}</Field>
          </Grid>
        )}
        {meta.replicationType && (
          <Grid item xs={12} sm={6}>
            <Field label="Replication">{meta.replicationType}</Field>
          </Grid>
        )}

        {/* Data Factory specific */}
        {meta.factoryEndpoint && (
          <Grid item xs={12}>
            <Field label="Factory Endpoint">{meta.factoryEndpoint}</Field>
          </Grid>
        )}
        {meta.factoryId && (
          <Grid item xs={12}>
            <Field label="Factory Resource ID">{meta.factoryId}</Field>
          </Grid>
        )}

        {/* IaC */}
        {meta.iacType && (
          <Grid item xs={12} sm={6}>
            <Field label="IaC">{String(meta.iacType)}</Field>
          </Grid>
        )}
        {meta.gitProvider && (
          <Grid item xs={12} sm={6}>
            <Field label="Git Provider">{String(meta.gitProvider)}</Field>
          </Grid>
        )}
      </Grid>
    </InfoCard>
  );
};
